import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { bookingApi } from '../../../utils/bookingApi';
import { formatPence, poundsToPence } from '../../../utils/money';
import { useAuth } from '../../../contexts/AuthContext';
import { PageHeader, Sheet, Skeleton, ErrorState, useConfirm, useToast } from '../../../components/ui';
import InvoiceDraftEditor from './InvoiceDraftEditor';
import CreditNoteForm from './CreditNoteForm';
import { invoiceStatus, formatDay } from './AdminInvoices';
import '../booking-shared.css';
import './AdminInvoices.css';

export const SKIPPED = {
  'no-email': 'the contact has no email address',
  'email-disabled': 'email is turned off for the club',
  'not-requested': 'you chose not to email it',
  failed: 'the email failed to send, so try Email again',
};
export const emailOutcome = (number, verb, { emailed, emailSkippedReason }) => (emailed
  ? `${number} ${verb} and emailed`
  : `${number} ${verb}. Not emailed: ${SKIPPED[emailSkippedReason] || 'unknown reason'}.`);

// Says what happened: a toast when it was emailed, or a note that stays on
// the page when it wasn't, so the reason can't vanish before it's read
const reportOutcome = (toast, setNotice, number, verb, result) => {
  if (result.emailed) {
    setNotice(null);
    toast(emailOutcome(number, verb, result));
  } else {
    toast(`${number} ${verb}`);
    setNotice(emailOutcome(number, verb, result));
  }
};

const BACK = { to: '/booking/admin/invoices', label: 'Invoices' };

// Taking credit on account off a sent invoice
function ApplyCreditSheet({ invoice, onApplied, onClose }) {
  const [amount, setAmount] = useState(String(Math.min(invoice.creditAvailable, invoice.amountDue) / 100));
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const submit = async () => {
    const pence = poundsToPence(amount);
    if (!pence || pence <= 0) { setError('Enter an amount above £0.'); return; }
    if (pence > invoice.creditAvailable) { setError(`That's more than the ${formatPence(invoice.creditAvailable)} on account.`); return; }
    if (pence > invoice.amountDue) { setError(`That's more than the ${formatPence(invoice.amountDue)} still due.`); return; }
    setError(null);
    setSaving(true);
    try {
      const res = await bookingApi.applyCredit(invoice.id, pence);
      await onApplied(res.data);
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't apply the credit.");
      setSaving(false);
    }
  };
  return (
    <Sheet
      title="Apply credit"
      onClose={onClose}
      onSubmit={submit}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={saving}>{saving ? 'Applying…' : 'Apply credit'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <p className="bk-small" style={{ marginTop: 0 }}>
        {formatPence(invoice.creditAvailable)} on account. {formatPence(invoice.amountDue)} still due.
      </p>
      <label className="bk-label">Credit to apply (£)
        <input className="bk-input" type="number" min="0.01" step="0.01" inputMode="decimal" required
          value={amount} onChange={e => setAmount(e.target.value)} />
        {error && <span className="bk-field-error" role="alert">{error}</span>}
      </label>
    </Sheet>
  );
}

function SentInvoice({ invoice, onChanged, notice, setNotice }) {
  // Emailing, marking paid and credit notes need Manage access to business
  const { can } = useAuth();
  const managesBusiness = can('business', 'MANAGE');
  const confirm = useConfirm();
  const toast = useToast();
  const [crediting, setCrediting] = useState(false);
  const [applying, setApplying] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const s = invoiceStatus(invoice);
  const settled = invoice.settled ?? invoice.total - invoice.credited;
  // What was still owed when the card payment came in (credit applied earlier had already reduced it)
  const dueWhenPaid = settled - (invoice.applied || 0);
  const paidByCard = invoice.status === 'PAID' && invoice.paidManually === false && invoice.paidAmount;
  const isSent = invoice.status === 'SENT';

  // Downloads pass track = false: they are safe to repeat and shouldn't lock the other buttons
  const run = async (fn, fallback, track = true) => {
    setError(null);
    if (track) setBusy(true);
    try { await fn(); } catch (err) {
      setError(err.response?.data?.error || fallback);
    } finally { if (track) setBusy(false); }
  };

  const copyLink = async () => {
    setError(null);
    try {
      await navigator.clipboard.writeText(invoice.payUrl);
      toast('Payment link copied');
    } catch {
      setError("Couldn't copy the link. Select it below and copy it by hand.");
    }
  };

  const download = () => run(
    () => bookingApi.downloadInvoicePdf(invoice.id, `${invoice.invoiceNumber}.pdf`),
    "Couldn't download the PDF. Try again.",
    false,
  );

  const emailAgain = () => run(async () => {
    const res = await bookingApi.emailInvoice(invoice.id);
    reportOutcome(toast, setNotice, invoice.invoiceNumber, 'sent', res.data);
  }, "Couldn't email the invoice.");

  const markPaid = async () => {
    const ok = await confirm({
      title: `Mark ${invoice.invoiceNumber} as paid?`,
      message: "Use this when it's been paid by bank transfer or cash. It can't have credit notes added afterwards.",
      confirmLabel: 'Mark as paid',
      tone: 'primary',
    });
    if (!ok) return;
    await run(async () => {
      await bookingApi.markInvoicePaid(invoice.id);
      toast(`${invoice.invoiceNumber} marked as paid`);
      await onChanged();
    }, "Couldn't mark the invoice as paid.");
  };

  return (
    <>
      <PageHeader back={BACK} title={invoice.invoiceNumber} />
      <div className="inv-card">
        <span className={`bk-pill bk-pill--${s.tone} inv-self-start`}>{s.label}</span>
        <span>
          <strong>{invoice.billToName}</strong>
          {invoice.billToEmail && <span className="inv-muted inv-small"> · {invoice.billToEmail}</span>}
        </span>
        <span className="inv-muted inv-small">
          Issued {formatDay(invoice.issueDate)} · Due {formatDay(invoice.dueDate)}
          {invoice.paidAt && ` · Paid ${formatDay(invoice.paidAt)}`}
        </span>
        {paidByCard && (
          <span className="inv-small">
            {`Paid by card ${formatPence(invoice.paidAmount)} on ${formatDay(invoice.paidAt)}`}
            {invoice.paidAmount > dueWhenPaid && ` (${formatPence(invoice.paidAmount - dueWhenPaid)} more than was due)`}
          </span>
        )}
      </div>

      {error && <p className="bk-form-error" role="alert">{error}</p>}
      {notice && <p className="inv-note" role="status">{notice}</p>}

      <div className="inv-card">
        <table className="inv-table">
          <thead><tr><th>Description</th><th className="num">Qty</th><th className="num">Amount</th></tr></thead>
          <tbody>
            {invoice.lines.map(l => (
              <tr key={l.id}>
                <td>{l.description}<br /><span className="inv-muted inv-small">{formatPence(l.unitAmount)} each</span></td>
                <td className="num">{l.quantity}</td>
                <td className="num">{l.kind === 'CREDIT' ? `-${formatPence(l.quantity * l.unitAmount)}` : formatPence(l.quantity * l.unitAmount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="inv-totals">
          {invoice.credits > 0 && <span className="inv-muted">{`Charges ${formatPence(invoice.charges)}`}</span>}
          {invoice.credits > 0 && <span className="inv-muted">{`Credits -${formatPence(invoice.credits)}`}</span>}
          <span>Total {formatPence(invoice.total)}</span>
          {invoice.credited > 0 && <span className="inv-muted">Credited -{formatPence(invoice.credited)}</span>}
          {(invoice.allocations || []).map(a => (
            <span key={a.id} className="inv-muted">Credit applied ({a.creditNote.creditNoteNumber}) -{formatPence(a.amount)}</span>
          ))}
          <span className="inv-muted inv-small">{invoice.status === 'PAID' ? 'Paid' : 'Amount due'}</span>
          <strong>{formatPence(invoice.status === 'PAID' ? settled : invoice.amountDue)}</strong>
        </div>
        {invoice.notes && <p className="inv-muted inv-small" style={{ margin: 0 }}>{invoice.notes}</p>}
      </div>

      {isSent && invoice.payUrl && (
        <div className="inv-card">
          <h2 className="bk-section-title">Payment link</h2>
          <span className="inv-muted inv-small">Customers can pay by card from this link.</span>
          <input className="bk-input" readOnly aria-label="Payment link" value={invoice.payUrl}
            onFocus={e => e.target.select()} style={{ width: '100%', boxSizing: 'border-box' }} />
          <button type="button" className="bk-btn bk-btn--sm inv-self-start" onClick={copyLink}>Copy link</button>
        </div>
      )}

      {managesBusiness && isSent ? (
        <div className="bk-actions">
          <button type="button" className="bk-btn bk-btn--primary" disabled={busy} onClick={markPaid}>Mark as paid</button>
          <button type="button" className="bk-btn" onClick={download}>Download PDF</button>
        </div>
      ) : (
        <div className="bk-actions">
          <button type="button" className="bk-btn" onClick={download}>Download PDF</button>
        </div>
      )}
      {managesBusiness && invoice.status !== 'VOID' && (
        <div className="bk-row-actions">
          <button type="button" className="bk-btn bk-btn--sm" disabled={busy} onClick={emailAgain}>Email again</button>
          {isSent && (
            <button type="button" className="bk-btn bk-btn--sm" onClick={() => setCrediting(true)}>Add credit note</button>
          )}
          {isSent && invoice.creditAvailable > 0 && (
            <button type="button" className="bk-btn bk-btn--sm" onClick={() => setApplying(true)}>Apply credit</button>
          )}
        </div>
      )}

      {applying && (
        <ApplyCreditSheet
          invoice={invoice}
          onClose={() => setApplying(false)}
          onApplied={async (updated) => {
            setApplying(false);
            toast(updated.status === 'PAID' ? `Credit applied. ${invoice.invoiceNumber} is now paid.` : 'Credit applied');
            await onChanged();
          }}
        />
      )}

      {crediting && (
        <CreditNoteForm
          invoice={invoice}
          onCancel={() => setCrediting(false)}
          onDone={async (result) => {
            setCrediting(false);
            reportOutcome(toast, setNotice, result.creditNote.creditNoteNumber, 'issued', result);
            await onChanged();
          }}
        />
      )}

      {invoice.creditNotes.length > 0 && (
        <div className="inv-card">
          <h2 className="bk-section-title">Credit notes</h2>
          {invoice.creditNotes.map(cn => (
            <button key={cn.id} type="button" className="inv-row" style={{ padding: '0.5rem 0' }}
              onClick={() => run(() => bookingApi.downloadCreditNotePdf(cn.id, `${cn.creditNoteNumber}.pdf`), `Couldn't download ${cn.creditNoteNumber}. Try again.`, false)}>
              <span className="inv-row__main">
                <span className="inv-row__name">{cn.creditNoteNumber}</span>
                <span className="inv-row__sub">{formatDay(cn.issueDate)}{cn.reason ? ` · ${cn.reason}` : ''}</span>
              </span>
              <span className="inv-row__amt">-{formatPence(cn.lines.reduce((sum, l) => sum + l.quantity * l.unitAmount, 0))}</span>
            </button>
          ))}
        </div>
      )}
    </>
  );
}

// Asks before sending: sending numbers the invoice and locks it
function SendSheet({ contact, sending, onSend, onClose }) {
  const [emailIt, setEmailIt] = useState(true);
  return (
    <Sheet
      title="Send this invoice?"
      onClose={onClose}
      onSubmit={() => onSend(emailIt)}
      footer={(requestClose) => (
        <div className="bk-actions">
          <button type="submit" className="bk-btn bk-btn--primary" disabled={sending}>{sending ? 'Sending…' : 'Send'}</button>
          <button type="button" className="bk-btn" onClick={requestClose}>Cancel</button>
        </div>
      )}
    >
      <p style={{ marginTop: 0 }}>Sending gives it a number and locks it. To change it afterwards you'll need a credit note.</p>
      {contact?.contactEmail && (
        <label className="inv-check">
          <input type="checkbox" checked={emailIt} onChange={e => setEmailIt(e.target.checked)} /> Email it to {contact.contactEmail}
        </label>
      )}
    </Sheet>
  );
}

export default function AdminInvoiceDetail() {
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === 'new';
  const [invoice, setInvoice] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [confirmSend, setConfirmSend] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const confirm = useConfirm();
  const toast = useToast();
  // Set when this page saves a new draft and moves to its URL, so the
  // effect doesn't reload it and overwrite a send that happened meanwhile
  const savedHere = useRef(null);
  // Drafts are made, changed, sent and deleted by staff who manage business
  const { can } = useAuth();
  const managesBusiness = can('business', 'MANAGE');

  const loadContacts = useCallback(() => bookingApi.getBusinessContacts().then(res => setContacts(res.data)), []);
  const loadInvoice = useCallback(async () => {
    if (isNew) return;
    const res = await bookingApi.getInvoice(id);
    setInvoice(res.data);
  }, [id, isNew]);

  useEffect(() => {
    setLoadError(false);
    Promise.all([loadContacts(), savedHere.current === id ? null : loadInvoice()])
      .catch(() => setLoadError(true))
      .finally(() => setLoaded(true));
  }, [id, isNew, loadContacts, loadInvoice, attempt]);

  const retry = () => { setLoaded(false); setAttempt(a => a + 1); };

  if (!loaded) {
    return (
      <div className="bk-page bk-page--wide inv-page">
        <PageHeader back={BACK} title={isNew ? 'New invoice' : 'Invoice'} />
        <Skeleton rows={4} label="Loading the invoice…" />
      </div>
    );
  }

  // An existing invoice that failed to load must not fall through to a blank editor,
  // where Save would create a second draft. A new one that couldn't load the
  // contacts can't be billed to anyone either.
  if (loadError && (!isNew ? !invoice : contacts.length === 0)) {
    return (
      <div className="bk-page bk-page--wide inv-page">
        <PageHeader back={BACK} title={isNew ? 'New invoice' : 'Invoice'} />
        <ErrorState title="Couldn't load the invoice" onRetry={retry} />
      </div>
    );
  }

  if (invoice && invoice.status !== 'DRAFT') {
    return (
      <div className="bk-page bk-page--wide inv-page">
        <SentInvoice invoice={invoice} onChanged={loadInvoice} notice={notice} setNotice={setNotice} />
      </div>
    );
  }

  const previewPdf = () => {
    setError(null);
    bookingApi.downloadInvoicePdf(invoice.id, 'draft-invoice.pdf').catch(() => setError("Couldn't download the PDF. Try again."));
  };

  if (!managesBusiness) {
    return (
      <div className="bk-page bk-page--wide inv-page">
        <PageHeader back={BACK} title={isNew ? 'New invoice' : 'Draft invoice'} />
        <p className="inv-muted">Only staff who manage business can make or change invoices.</p>
        {error && <p className="bk-form-error" role="alert">{error}</p>}
        {invoice && (
          <div className="bk-actions">
            <button type="button" className="bk-btn" onClick={previewPdf}>Preview PDF</button>
          </div>
        )}
      </div>
    );
  }

  const onSaved = (saved) => {
    setInvoice(saved);
    savedHere.current = saved.id;
    if (isNew) navigate(`/booking/admin/invoices/${saved.id}`, { replace: true });
  };

  const send = async (save, contact, emailIt) => {
    setError(null);
    setSending(true);
    try {
      const saved = await save({ forSend: true });
      if (!saved) { setConfirmSend(false); return; }
      // Only ask for no email when the checkbox was shown and unticked, so a contact
      // with no address is reported as having no email rather than as not requested
      const res = await bookingApi.sendInvoice(saved.id, { email: contact?.contactEmail ? emailIt : true });
      setConfirmSend(false);
      setInvoice(res.data.invoice);
      reportOutcome(toast, setNotice, res.data.invoice.invoiceNumber, 'sent', res.data);
    } catch (err) {
      setConfirmSend(false);
      setError(err.response?.data?.error || "Couldn't send the invoice.");
      // The save may have worked, so show the real state
      await loadInvoice().catch(() => {});
    } finally {
      setSending(false);
    }
  };

  const remove = async () => {
    const ok = await confirm({
      title: 'Delete this draft?',
      message: 'It has no number yet, so nothing is lost from the sequence.',
      confirmLabel: 'Delete draft',
    });
    if (!ok) return;
    setError(null);
    try {
      await bookingApi.deleteInvoice(invoice.id);
      toast('Draft deleted');
      navigate('/booking/admin/invoices');
    } catch (err) {
      setError(err.response?.data?.error || "Couldn't delete the draft.");
    }
  };

  return (
    <div className="bk-page bk-page--wide inv-page">
      <PageHeader back={BACK} title={isNew ? 'New invoice' : 'Draft invoice'} details="Not sent yet." />
      <InvoiceDraftEditor
        key={invoice?.id || 'new'}
        invoice={invoice}
        contacts={contacts}
        defaultContactId={searchParams.get('contact')}
        onSaved={(saved) => { onSaved(saved); if (!confirmSend) toast('Draft saved'); }}
        onContactsChanged={loadContacts}
        busy={sending}
        renderActions={({ save, saving, contact }) => (
          <>
            {error && <p className="bk-form-error" role="alert">{error}</p>}
            <div className="bk-actions">
              <button type="button" className="bk-btn bk-btn--primary" disabled={saving || sending} onClick={() => setConfirmSend(true)}>Send invoice</button>
              <button type="button" className="bk-btn" disabled={saving || sending} onClick={() => save()}>Save draft</button>
            </div>
            {invoice && (
              <div className="bk-row-actions">
                <button type="button" className="bk-btn bk-btn--sm" onClick={previewPdf}>Preview PDF</button>
                <button type="button" className="bk-btn bk-btn--sm bk-btn--outline-danger" disabled={sending} onClick={remove}>Delete draft</button>
              </div>
            )}
            {confirmSend && (
              <SendSheet contact={contact} sending={saving || sending}
                onSend={(emailIt) => send(save, contact, emailIt)} onClose={() => setConfirmSend(false)} />
            )}
          </>
        )}
      />
    </div>
  );
}
